<template>
  <div>
    <h2 class="mb-4">Heats of Solution: Results</h2>

    <p class="mb-5">
      Enter all of the data collected during the simulation in the table below. Note that the heat
      capacity of both the solution inside the can and that in the cooling jacket can be assumed to
      be the same as water, i.e.
      <stemble-latex content="$\text{4.184 J}\,\text{g}^{-1 \, \circ}\text{C}^{-1}.$" />
      The heat capacity of the aluminum can can be assumed to be
      <stemble-latex content="$\text{0.900 J}\,\text{g}^{-1 \, \circ}\text{C}^{-1}.$" />
    </p>

    <v-simple-table>
      <thead>
        <tr>
          <td style="width: 16%">
            <stemble-latex content="$\text{Variable}$" />
          </td>
          <td style="text-align: center; width: 21%">
            <stemble-latex content="$\ce{MgSO4}$" />
          </td>
          <td style="text-align: center; width: 21%">
            <stemble-latex content="$\ce{NH4NO3}$" />
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Mass of salt (g)</td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.massSalt1"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.massSalt2"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
        </tr>

        <tr>
          <td>Mass of water (g)</td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.massWater1"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.massWater2"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
        </tr>

        <tr>
          <td>
            <stemble-latex content="$\text{T}_\text{i}$" />
            <stemble-latex content="$\,(^\circ\text{C})$" />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.Ti1"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.Ti2"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
        </tr>

        <tr>
          <td>
            <stemble-latex content="$\text{T}_\text{f}$" />
            <stemble-latex content="$\,(^\circ\text{C})$" />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.Tf1"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.Tf2"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
        </tr>

        <tr>
          <td>
            <stemble-latex content="$\Delta\text{T}$" />
            <stemble-latex content="$\,(^\circ\text{C})$" />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.deltaT1"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.deltaT2"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
        </tr>

        <tr>
          <td>
            <stemble-latex content="$\text{q}_\text{soln}$" />
            <stemble-latex content="$\,(\text{J})$" />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.qRxn1"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.qRxn2"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
        </tr>

        <tr>
          <td>
            <stemble-latex content="$\Delta\text{H}_\text{rxn,exp}$" />
            <stemble-latex content="$\,(\text{J/mol})$" />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.deltaHrxn1"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.deltaHrxn2"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
        </tr>

        <tr>
          <td style="width: 16%" class="py-2 my-2">
            <stemble-latex content="$\Delta\text{H}_\text{rxn,lit}$" />
            <stemble-latex content="$\,(\text{J/mol})$" />
          </td>
          <td style="text-align: center; width: 21%" class="py-2 my-2">
            <stemble-latex content="$-84,900$" />
          </td>
          <td style="text-align: center; width: 21%" class="py-2 my-2">
            <stemble-latex content="$+26,400$" />
          </td>
        </tr>

        <tr>
          <td>Percent Error <stemble-latex content="$(\%)$" /></td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.pctError1"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
          <td class="centered-input px-2 py-2 mx-0 my-2">
            <calculation-input
              v-model="inputs.pctError2"
              :show-scientific-notation="false"
              :disabled="!allowEditing"
            />
          </td>
        </tr>
      </tbody>
    </v-simple-table>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';
import ChemicalNotationInput from '@/chemical-equations/components/ChemicalNotationInput.vue';
import ChemicalSymbolInput from '@/tasks/components/inputs/ChemicalSymbolInput.vue';
import NumberValue from '@/tasks/components/NumberValue.vue';
import LatexNumber from '@/tasks/components/displayers/LatexNumber.vue';
import STextarea from '@/common/components/STextareaShort.vue';

export default {
  name: 'UBC_2025_CalorimetryLab_Results',
  components: {
    STextarea,
    LatexNumber,
    NumberValue,
    ChemicalSymbolInput,
    ChemicalNotationInput,
    CalculationInput,
    StembleLatex,
  },
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      // Put a list of inputs or computed properties here to be sent for marking
      inputs: {
        massSalt1: null,
        massWater1: null,
        Ti1: null,
        Tf1: null,
        deltaT1: null,
        qRxn1: null,
        deltaHrxn1: null,
        pctError1: null,

        massSalt2: null,
        massWater2: null,
        Ti2: null,
        Tf2: null,
        deltaT2: null,
        qRxn2: null,
        deltaHrxn2: null,
        pctError2: null,
      },
    };
  },
};
</script>
<style scoped></style>
